import React, { createContext, useState, useContext, useEffect } from 'react';
import apiService from '../services/api.service';
import { LoginRequest } from '../dto/auth/LoginRequest';

/**
 * Authentication context for managing user state across the application.
 */
const AuthContext = createContext(null);

/**
 * Custom hook to use auth context.
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/**
 * Authentication provider component.
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Initialize auth state from localStorage on mount
  useEffect(() => {
    const initAuth = () => {
      try {
        const userData = apiService.getUserData();
        const authenticated = apiService.isAuthenticated();
        
        if (authenticated && userData) {
          setUser(userData);
          setIsAuthenticated(true);
        }
      } catch (err) {
        console.error('Error initializing auth:', err);
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, []);

  /**
   * Performs login with email and password.
   */
  const login = async (email, password, recaptchaToken = '') => {
    setError(null);
    setIsLoading(true);

    try {
      const loginRequest = new LoginRequest(email, password, recaptchaToken);
      const result = await apiService.login(loginRequest);

      if (result.success && result.data) {
        // Store authentication data
        apiService.storeAuthData(result.data);
        
        // Fetch fresh user profile from backend
        const profileResult = await apiService.getCurrentUser();
        
        if (profileResult.success && profileResult.data) {
          // Update state with fresh profile data
          setUser({
            userId: profileResult.data.userId,
            name: profileResult.data.name,
            email: profileResult.data.email,
            tenantId: profileResult.data.tenantId,
            roles: profileResult.data.roles,
            onboardingPending: profileResult.data.onboardingPending,
          });
          setIsAuthenticated(true);
          
          return { success: true, onboardingPending: Boolean(profileResult.data.onboardingPending) };
        } else {
          // Fallback to login response data if profile fetch fails
          setUser({
            userId: result.data.userId,
            name: result.data.name,
            email: result.data.email,
            tenantId: result.data.tenantId,
            roles: result.data.roles,
            onboardingPending: result.data.onboardingPending,
          });
          setIsAuthenticated(true);
          
          return { success: true, onboardingPending: Boolean(result.data.onboardingPending) };
        }
      } else {
        const errorMessage = result.error?.getUserMessage() || 'Login failed';
        setError(errorMessage);
        return { 
          success: false, 
          error: errorMessage 
        };
      }
    } catch (err) {
      const errorMessage = 'An unexpected error occurred during login';
      setError(errorMessage);
      console.error('Login error:', err);
      return { 
        success: false, 
        error: errorMessage 
      };
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Performs logout.
   */
  const logout = async () => {
    setIsLoading(true);
    
    try {
      await apiService.logout();
      setUser(null);
      setIsAuthenticated(false);
      setError(null);
      return { success: true };
    } catch (err) {
      console.error('Logout error:', err);
      return { 
        success: false, 
        error: 'Failed to logout properly' 
      };
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Completes authentication using an already-issued login payload (e.g. onboarding verify-email).
   */
  const completeOnboardingAuth = (loginResponse) => {
    try {
      apiService.storeAuthData(loginResponse);
      setUser({
        userId: loginResponse.userId,
        name: loginResponse.name,
        email: loginResponse.email,
        tenantId: loginResponse.tenantId,
        roles: loginResponse.roles,
        onboardingPending: Boolean(loginResponse.onboardingPending),
      });
      setIsAuthenticated(true);
      setError(null);
      return { success: true };
    } catch (err) {
      console.error('Onboarding auth completion error:', err);
      return {
        success: false,
        error: 'Failed to complete onboarding authentication'
      };
    }
  };

  /**
   * Clears any error state.
   */
  const clearError = () => {
    setError(null);
  };

  const value = {
    user,
    isAuthenticated,
    isLoading,
    error,
    login,
    completeOnboardingAuth,
    logout,
    clearError
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
